<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import type { MaintenanceItem, MaintenanceLanguage } from "../../types/TextMaintenanceTypes";

const props = defineProps({
    data: {
        type: Array as () => MaintenanceItem[],
        required: true,
    },
    selectedLanguages: {
        type: Array as () => string[],
        required: true,
    },
    availableLanguages: {
        type: Array as () => MaintenanceLanguage[],
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["delete-item", "update-item"]);

const { t } = useI18n();

const editingCell = ref<{ itemId: string; language: string } | null>(null);
const editValue = ref("");

// Row styling function similar to TextDiffs
const getRowClass = (rowData: MaintenanceItem): string => {
    // Check if item is deleted first (highest priority)
    if (rowData.isDeleted) {
        return 'maintenance-row-deleted';
    }
    // Check if item has any changes
    if (hasChanges(rowData)) {
        return 'maintenance-row-changed';
    }
    return 'maintenance-row-unchanged';
};

// Check if a specific language text is new (imported)
const isTextNew = (item: MaintenanceItem, language: string): boolean => {
    // Check for language-specific new status
    if (item.newTexts && item.newTexts[language]) {
        return true;
    }
    // Fallback to old behavior for backward compatibility
    return !!(item.isNew && item.texts[language]);
};

// Check if a specific language text has been changed
const isTextChanged = (item: MaintenanceItem, language: string): boolean => {
    return item.changedTexts?.[language] === true;
};

// Check if an item has any changes
const hasChanges = (item: MaintenanceItem): boolean => {
    return Object.values(item.changedTexts || {}).includes(true);
};

// Check if we should show the original text for a given item and language
const showOriginalText = (itemId: string, language: string): boolean => {
    const item = props.data.find(i => i.id === itemId);
    return !!(item && item.originalTexts && item.originalTexts[language] && isTextChanged(item, language));
};

// Get the original text for a given item and language
const getOriginalText = (itemId: string, language: string): string => {
    const item = props.data.find(i => i.id === itemId);
    return item?.originalTexts?.[language] || '';
};

/**
 * Reset a specific item to its original texts
 */
const handleResetItem = (item: MaintenanceItem) => {
    const wasEditing = editingCell.value && editingCell.value.itemId === item.id;
    if (wasEditing) {
        editingCell.value = null;
        editValue.value = "";
    }
    
    // Create new texts object with original values restored
    const restoredTexts = { ...item.texts };
    const resetChangedTexts = { ...item.changedTexts };
    
    // Reset texts to original values if available
    if (item.originalTexts) {
        for (const language of Object.keys(item.originalTexts)) {
            if (item.originalTexts[language] !== undefined) {
                restoredTexts[language] = item.originalTexts[language];
                resetChangedTexts[language] = false;
            }
        }
    }
    
    // Create updated item with restored values
    const updatedItem = {
        ...item,
        texts: restoredTexts,
        changedTexts: resetChangedTexts,
    };

    // Emit update to parent
    emit("update-item", updatedItem);
};

// Check if there are any changed items
const hasAnyChanges = computed(() => {
    return props.data.some(item => hasChanges(item));
});

// Helper function to reset a single item
function resetSingleItem(item: any) {
    if (!hasChanges(item)) return null;
    
    // Create new texts object with original values restored
    const restoredTexts = { ...item.texts };
    const resetChangedTexts = { ...item.changedTexts };
    
    // Reset texts to original values if available
    if (item.originalTexts) {
        for (const language of Object.keys(item.originalTexts)) {
            if (item.originalTexts && item.originalTexts[language] !== undefined) {
                restoredTexts[language] = item.originalTexts[language];
                resetChangedTexts[language] = false;
            }
        }
    }
    
    // Create updated item with restored values
    return {
        ...item,
        texts: restoredTexts,
        changedTexts: resetChangedTexts,
    };
}

// Reset all changed items at once
const handleResetAllItems = () => {
    // Exit edit mode if currently editing
    if (editingCell.value) {
        editingCell.value = null;
        editValue.value = "";
    }
    
    // Reset all items that have changes
    for (const item of props.data) {
        const updatedItem = resetSingleItem(item);
        if (updatedItem) {
            // Emit update for each item
            emit("update-item", updatedItem);
        }
    }
    
    console.log('Reset all changed items to original values');
};

const emptyMessage = computed(() => {
    return t('textMaintenance.table.noData');
});

const getLanguageName = (languageCode: string): string => {
    const translationKey = `languageNames.${languageCode.toLowerCase()}`;
    const translatedName = t(translationKey);
    
    // If translation exists and is not the same as the key, use it
    // Otherwise fallback to uppercase code
    return translationKey === translatedName ? languageCode.toUpperCase() : translatedName;
};

const handleDeleteItem = (item: MaintenanceItem) => {
    emit("delete-item", item.id);
};

const startEdit = (itemId: string, language: string, currentValue: string) => {
    editingCell.value = { itemId, language };
    editValue.value = currentValue || "";
};

const cancelEdit = () => {
    editingCell.value = null;
    editValue.value = "";
};

const saveEdit = () => {
    if (!editingCell.value) return;
    
    const { itemId, language } = editingCell.value;
    const item = props.data.find(item => item.id === itemId);
    
    if (item) {
        const originalValue = item.texts[language] || "";
        const newValue = editValue.value;
        
        // Prepare originalTexts object
        const currentOriginalTexts = item.originalTexts || {};
        
        // Store original value if this is the first edit for this language
        if (currentOriginalTexts[language] === undefined) {
            currentOriginalTexts[language] = originalValue;
        }
        
        // Only mark as changed if the value actually changed from original
        const originalStoredValue = currentOriginalTexts[language] || "";
        const hasChanged = originalStoredValue !== newValue;
        
        const updatedItem = {
            ...item,
            texts: {
                ...item.texts,
                [language]: newValue,
            },
            originalTexts: currentOriginalTexts,
            changedTexts: {
                ...item.changedTexts,
                [language]: hasChanged,
            },
        };
        
        console.log(`Saved edit for ${itemId} [${language}]:`, {
            original: originalStoredValue,
            new: newValue,
            changed: hasChanged
        });
        
        emit("update-item", updatedItem);
    }
    
    cancelEdit();
};

const isEditing = (itemId: string, language: string): boolean => {
    return editingCell.value?.itemId === itemId && editingCell.value?.language === language;
};

/**
 * Handle specific key events in the text area
 */
const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        saveEdit();
    } else if (event.key === "Escape") {
        cancelEdit();
    }
};

const copyToClipboard = async (item: MaintenanceItem) => {
    const content = Object.entries(item.texts)
        .map(([lang, text]) => `${lang}: ${text || ''}`)
        .join('\n');
    await navigator.clipboard.writeText(content);
};
</script>

<template>
    <div class="maintenance-table">
        <DataTable
            :value="data"
            data-key="id"
            :loading="loading"
            striped-rows
            responsive-layout="scroll"
            removableSort
            :empty-message="emptyMessage"
            class="p-datatable-sm"
            :row-class="getRowClass"
            sort-field="identifier"
            :sort-order="1"
            data-cy="text-maintenance_data-table"
        >
            <!-- Identifier Column -->
            <Column
                field="identifier"
                :header="t('textMaintenance.table.identifier')"
                sortable
                style="min-width: 200px"
            >
                <template #body="{ data: item }">
                    <span class="font-medium identifier-text">{{ item.identifier }}</span>
                </template>
            </Column>

            <!-- Language Columns -->
            <Column
                v-for="language in selectedLanguages"
                :key="language"
                :field="language"
                :header="getLanguageName(language)"
                style="min-width: 250px"
            >
                <template #body="{ data: item }">
                    <div 
                        class="text-cell"
                        :class="{ 'changed': isTextChanged(item, language), 'new': isTextNew(item, language) }"
                        :data-cy="`text-maintenance_text-cell-${item.identifier}-${language}`"
                    >
                        <div
                            v-if="!isEditing(item.id, language)"
                            class="text-content"
                            :class="{ 'editable': !item.isDeleted }"
                            @click="!item.isDeleted && startEdit(item.id, language, item.texts[language])"
                            :data-cy="`text-maintenance_text-content-${item.identifier}-${language}`"
                        >
                            <span v-if="item.isDeleted && item.originalTexts?.[language]" class="text-value">
                                {{ item.originalTexts[language] }}
                            </span>
                            <span v-else-if="item.texts[language]" class="text-value">
                                {{ item.texts[language] }}
                            </span>
                            <span v-else-if="!item.isDeleted" class="text-placeholder">
                                {{ t('textMaintenance.table.clickToEdit') }}
                            </span>
                            <i v-if="!item.isDeleted" class="pi pi-pencil text-content-muted ml-2 edit-icon"></i>
                        </div>
                        
                        <div v-else class="edit-mode">
                            <Textarea
                                v-model="editValue"
                                class="w-full"
                                :placeholder="t('textMaintenance.table.enterText')"
                                @keydown="handleKeyDown"
                                autofocus
                                auto-resize
                                rows="3"
                                :data-cy="`text-maintenance_text-input-${item.identifier}-${language}`"
                            />
                            
                            <!-- Show original text if this is a modified cell -->
                            <div v-if="showOriginalText(editingCell?.itemId || '', editingCell?.language || '')"
                                 :data-cy="`text-maintenance_original-text-${item.identifier}-${editingCell?.language}`"
                                 class="text-xs text-content-muted mt-1 p-2 bg-gray-50 rounded border-l-3 border-blue-300">
                                <strong>{{ t('textMaintenance.table.originalText') }}:</strong> 
                                {{ getOriginalText(editingCell?.itemId || '', editingCell?.language || '') }}
                            </div>
                            
                            <div class="edit-actions mt-1">
                                <Button
                                    icon="pi pi-check"
                                    size="small"
                                    severity="success"
                                    @click="saveEdit"
                                    class="mr-1"
                                    :data-cy="`text-maintenance_save-edit-button-${item.identifier}-${language}`"
                                />
                                <Button
                                    icon="pi pi-times"
                                    size="small"
                                    severity="secondary"
                                    @click="cancelEdit"
                                    :data-cy="`text-maintenance_cancel-edit-button-${item.identifier}-${language}`"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </Column>

            <!-- Actions Column - Always show -->
            <Column
                field="actions"
                :header="t('textMaintenance.table.actions')"
                style="min-width: 150px"
            >
                <template #body="{ data: item }">
                    <div class="flex gap-1">
                        <!-- Copy Button -->
                        <Button
                            icon="pi pi-copy"
                            size="small"
                            severity="secondary"
                            @click="copyToClipboard(item)"
                            v-tooltip="t('textMaintenance.table.copyToClipboard')"
                            class="p-button-outlined"
                            :data-cy="`text-maintenance_copy-item-button-${item.identifier}`"
                        />
                        <!-- Reset Button (only show if item has changes) -->
                        <Button
                            v-if="hasChanges(item)"
                            icon="pi pi-refresh"
                            size="small"
                            severity="secondary"
                            @click="handleResetItem(item)"
                            v-tooltip="t('textMaintenance.table.resetToOriginal')"
                            class="p-button-outlined"
                            :data-cy="`text-maintenance_reset-item-button-${item.identifier}`"
                        />
                        <!-- Delete Button -->
                        <Button
                            icon="pi pi-trash"
                            size="small"
                            severity="danger"
                            @click="handleDeleteItem(item)"
                            v-tooltip="t('textMaintenance.table.deleteItem')"
                            class="p-button-outlined"
                            :data-cy="`text-maintenance_delete-item-button-${item.identifier}`"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>

        <!-- Action Buttons -->
        <div class="mt-4 flex justify-center gap-2">
            <!-- Reset All Changes Button -->
            <Button
                v-if="hasAnyChanges"
                :label="t('textMaintenance.table.resetAllChanges')"
                icon="pi pi-refresh"
                severity="warn"
                @click="handleResetAllItems"
                class="p-button-outlined"
                :title="t('textMaintenance.table.resetAllChangesTooltip')"
                data-cy="text-maintenance_reset-all-items-button"
            />
            

        </div>
    </div>
</template>

<style scoped>
.maintenance-table {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    /* Color variables for maintenance states (matching TextDiffs) */
    --diff-unchanged-hover: #f3f4f6;
    --diff-changed-bg: #fef3c7;
    --diff-changed-text: #965000;
    --diff-changed-border: #fbbf24;
    --diff-changed-hover: #fcedb2;
    --diff-changed-text-bg: transparent;
    --diff-changed-hover-bg: #feeca5;
    --diff-added-bg: #dcfce7;
    --diff-added-text: #15803d;
    --diff-added-border: #4ade80;
    --diff-added-hover: #cfffe0;
}

.text-cell {
    position: relative;
    min-height: 2.5rem;
}

.text-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    border: 1px solid transparent;
    border-radius: 4px;
    transition: all 0.2s ease;
    min-height: 2.5rem;
}

.text-content.editable {
    cursor: pointer;
}

.text-content.editable:hover {
    background-color: var(--p-gray-50);
    border-color: var(--p-gray-200);
}

.text-content:not(.editable) {
    cursor: default;
}

.text-value {
    flex: 1;
    word-break: break-word;
    white-space: pre-wrap;
}

.text-placeholder {
    flex: 1;
    color: var(--p-gray-400);
    font-style: italic;
}

.edit-icon {
    opacity: 0;
    transition: opacity 0.2s ease;
    font-size: 0.8rem;
}

.text-content.editable:hover .edit-icon {
    opacity: 1;
}

.edit-mode {
    padding: 0.25rem;
}

.edit-actions {
    display: flex;
    justify-content: flex-end;
}

/* Row styling for changed items (matching TextDiffs pattern) */
:deep(.maintenance-row-unchanged) {
    background-color: transparent !important;
}

:deep(.maintenance-row-changed) {
    background-color: transparent !important;
}

:deep(.maintenance-row-added) {
    background-color: transparent !important; /* Remove row-level highlighting for new items */
}

:deep(.maintenance-row-deleted) {
    background-color: #ffebee !important;
    height: 30px !important;
    opacity: 0.6 !important;
    border-left: 3px solid #f44336 !important;
}

:deep(.maintenance-row-deleted td) {
    padding: 0.25rem 0.75rem !important;
    text-decoration: line-through !important;
    color: #666 !important;
}

:deep(.maintenance-row-unchanged:hover) {
    background-color: var(--diff-unchanged-hover) !important;
}

:deep(.maintenance-row-changed:hover) {
    background-color: var(--diff-unchanged-hover) !important;
}

:deep(.maintenance-row-added:hover) {
    background-color: var(--diff-unchanged-hover) !important;
}

:deep(.maintenance-row-deleted:hover) {
    background-color: #ffcdd2 !important;
}

/* Highlight individual changed text cells */
.text-cell.changed {
    background-color: var(--diff-changed-bg);
    border: 1px solid var(--diff-changed-border);
    border-radius: 4px;
}

.text-cell.changed:hover {
    background-color: var(--diff-changed-hover);
}

/* Highlight individual new text cells */
.text-cell.new {
    background-color: var(--diff-added-bg);
    border: 1px solid var(--diff-added-border);
    border-radius: 4px;
}

.text-cell.new:hover {
    background-color: var(--diff-added-hover);
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem;
    vertical-align: top;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
    background-color: var(--p-gray-100);
    font-weight: 600;
    padding: 1rem 0.75rem;
}

:deep(.p-button-sm) {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
}

/* Reset All Button Styling */
.p-button.p-button-warn.p-button-outlined {
    color: var(--p-orange-600);
    border-color: var(--p-orange-600);
}

.p-button.p-button-warn.p-button-outlined:hover {
    background-color: var(--p-orange-50);
    color: var(--p-orange-700);
    border-color: var(--p-orange-700);
}

/* Programming font for identifiers */
.identifier-text {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    font-size: 0.9rem;
    background-color: #f8f9fa;
    padding: 2px 6px;
    border-radius: 4px;
    color: #2d3748;
    font-weight: 500;
}
</style>